import { useState } from "react";
import { RotateCcw, ShieldCheck } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { roleLabel } from "@/components/UserMenu";
import { TaskCard } from "@/components/tasks/TaskCard";

export const CeoOverview = ({ overview, isAdmin, onOpen, onChanged }) => {
  const [reopening, setReopening] = useState(null);
  const [comment, setComment] = useState("");
  const [error, setError] = useState("");

  async function verify(task) {
    try {
      await api.post(`/tasks/${task.id}/verify`);
      await onChanged(`Verified: ${task.title}`);
    } catch (requestError) {
      setError(formatApiError(requestError));
    }
  }

  async function reopen(task) {
    try {
      await api.post(`/tasks/${task.id}/reopen`, { comment });
      setReopening(null);
      setComment("");
      await onChanged(`Reopened and returned to ${task.owner_name}.`);
    } catch (requestError) {
      setError(formatApiError(requestError));
    }
  }

  if (!overview) return <p className="projection-loading">Loading overview…</p>;
  const { totals, by_owner: byOwner, verification_queue: queue } = overview;
  return (
    <section className="task-overview" data-testid="task-ceo-overview">
      <div className="credential-summary-grid">
        <article className="credential-metric credential-metric-teal" data-testid="overview-open"><span>Open</span><strong>{totals.open}</strong></article>
        <article className={`credential-metric ${totals.overdue ? "credential-metric-red" : ""}`} data-testid="overview-overdue"><span>Overdue</span><strong>{totals.overdue}</strong></article>
        <article className="credential-metric credential-metric-gold" data-testid="overview-awaiting"><span>Awaiting verification</span><strong>{totals.awaiting_verification}</strong></article>
        <article className="credential-metric credential-metric-teal" data-testid="overview-completed"><span>Completed this week</span><strong>{totals.completed_this_week}</strong></article>
      </div>
      <div className="credential-table-wrap" data-testid="overview-by-owner">
        <table>
          <thead><tr><th>Owner</th><th>Role</th><th>Open</th><th>Overdue</th><th>Blocked</th><th>Awaiting verification</th><th>Completed this week</th></tr></thead>
          <tbody>
            {byOwner.map((row) => (
              <tr key={row.owner_id} data-testid={`overview-owner-${row.owner_id}`}>
                <td><b>{row.owner_name}</b></td><td>{roleLabel(row.owner_role)}</td><td>{row.open}</td><td>{row.overdue ? <span className="credential-blocked">{row.overdue}</span> : 0}</td><td>{row.blocked}</td><td>{row.awaiting_verification}</td><td>{row.completed_this_week}</td>
              </tr>
            ))}
          </tbody>
        </table>
        {!byOwner.length && <p className="operations-empty-state">No tasks yet.</p>}
      </div>
      <section className="task-verification-queue" data-testid="task-verification-queue">
        <div className="section-heading"><div><h3><ShieldCheck size={16} /> CEO verification queue</h3><p>Tasks marked done wait here until the System Administrator verifies them or reopens them with a comment.</p></div></div>
        {error && <p className="form-message error-message" data-testid="verification-queue-error">{error}</p>}
        <div className="task-queue-list">
          {queue.map((task) => (
            <div className="task-queue-row" key={task.id} data-testid={`verification-row-${task.id}`}>
              <TaskCard task={task} onOpen={onOpen} compact />
              {isAdmin && (
                <div className="task-queue-actions">
                  <button type="button" className="button button-primary" data-testid={`queue-verify-${task.id}`} onClick={() => verify(task)}><ShieldCheck size={14} /> Verify</button>
                  <button type="button" className="button button-secondary" data-testid={`queue-reopen-${task.id}`} onClick={() => setReopening(reopening === task.id ? null : task.id)}><RotateCcw size={14} /> Reopen</button>
                  {reopening === task.id && (
                    <div className="evaluation-action-form">
                      <label>Comment for {task.owner_name} (required)<textarea data-testid={`queue-reopen-comment-${task.id}`} rows={2} value={comment} onChange={(e) => setComment(e.target.value)} /></label>
                      <button type="button" className="button button-primary" data-testid={`queue-reopen-submit-${task.id}`} disabled={comment.trim().length < 3} onClick={() => reopen(task)}>Reopen and return</button>
                    </div>
                  )}
                </div>
              )}
            </div>
          ))}
          {!queue.length && <p className="pairing-meta" data-testid="verification-queue-empty">Nothing awaiting verification.</p>}
        </div>
      </section>
    </section>
  );
};
